import { call, put, takeLatest, select } from "redux-saga/effects";
import { wizardURL } from "../../utils/config";
import {
  fetchHistoryRequest,
  fetchHistorySuccess,
  fetchHistoryFailure,
  sendMessageRequest,
  sendMessageSuccess,
  sendMessageFailure,
  appendUserMessage,
  fetchSuggestionsRequest,
  fetchSuggestionsSuccess,
  fetchSuggestionsFailure
} from "./agentsSlice";
import TokenManager from "../../utils/tokenManager";

function normalizeSources(rawSources) {
  if (!Array.isArray(rawSources)) return [];

  return rawSources
    .map((src, index) => {
      if (typeof src === "string") {
        return {
          id: src,
          title: `Source ${index + 1}`,
          snippet: "",
          sourceType: "pdf",
        };
      }

      if (!src || typeof src !== "object") return null;

      const sourceRef = src.source_reference || {};
      const id = src.id ?? sourceRef.id ?? sourceRef.entry_id;
      if (!id) return null;

      return {
        id: String(id),
        title:
          src.title ||
          sourceRef.title ||
          src.text ||
          `Source ${index + 1}`,
        snippet: src.snippet || sourceRef.brief_description || "",
        sourceType: src.source_type || sourceRef.source_type || "solution",
      };
    })
    .filter(Boolean);
}


function* fetchSuggestionsSaga(action) {
  try {
    const { projectId, challengeId } = action.payload;

    const token = TokenManager.getAccessToken();
    if (!token) throw new Error("Not authenticated");

    const response = yield call(fetch, `${wizardURL}/wizard/chat/suggest/${projectId}/${challengeId}`, {
      method: "GET",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      }
    });

    if (!response.ok) throw new Error("Failed to load suggestions");
    
    const data = yield response.json();
    yield put(fetchSuggestionsSuccess(data));
  } catch (error) {
    yield put(fetchSuggestionsFailure(error.message));
  }
}

function* fetchHistorySaga(action) {
  try {
    const { projectId, mode, challengeId } = action.payload; 
    const token = TokenManager.getAccessToken();
    if (!token || !projectId) return;

    const threadId = mode === "pdf" ? `${projectId}_pdf` : challengeId;
    
    const historyUrl = mode === "pdf"
      ? `${wizardURL}/wizard/chat/history/pdf/${projectId}`
      : `${wizardURL}/wizard/chat/history/${threadId}`;

    const response = yield call(fetch, historyUrl, {
      method: "GET",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      }
    });

    if (!response.ok) throw new Error("Failed to load history");

    const data = yield response.json();
    const historyMessages = Array.isArray(data) ? data : Array.isArray(data?.messages) ? data.messages : [];

    const formattedHistory = historyMessages.map((msg) => {
      // Safely extract content and role, handling both flat dicts and nested Langchain serializations
      let content = msg.content ?? (msg.data && msg.data.content) ?? "";
      let roleType = msg.role ?? msg.type ?? (msg.data && msg.data.type) ?? "assistant";
      let sources = [];

      // Prefer structured metadata when available (current backend format).
      if (msg?.metadata?.knowledge_used && Array.isArray(msg.metadata.knowledge_used)) {
        sources = normalizeSources(msg.metadata.knowledge_used);
      }

      // PDF-specific history metadata.
      if (msg?.metadata?.pdf_sources && Array.isArray(msg.metadata.pdf_sources)) {
        sources = normalizeSources(msg.metadata.pdf_sources);
      }

      // 1. Check if this message has hidden metadata 
      if (typeof content === "string" && content.includes("|||METADATA|||")) {
        const parts = content.split("|||METADATA|||");
        content = parts[0]; 
        
        try {
          const metaJson = JSON.parse(parts[1]);
          sources = normalizeSources(metaJson);
        } catch (e) {
          console.error("Failed to parse history metadata", e);
        }
      }

      return {
        role: roleType === "human" || roleType === "user" ? "user" : "assistant",
        content: content,
        sources: sources, 
      };
    });

    yield put(fetchHistorySuccess(formattedHistory));
  } catch (error) {
    console.error("History Fetch Error:", error);
    yield put(fetchHistoryFailure(error.message));
  }
}

function* sendMessageSaga(action) {
  try {
    const { projectId, message, mode, documentIds, challengeId } = action.payload;
    const token = TokenManager.getAccessToken();
    if (!token) throw new Error("Not authenticated");

    if (message.trim() !== "") {
      yield put(appendUserMessage(message));
    }

    let endpoint = mode === "pdf" ? `${wizardURL}/wizard/chat/pdf` : `${wizardURL}/wizard/chat/solutions`;
    // Removed user_id from the body
    let body = { project_id: projectId, message };
    
    if (mode === "pdf") body.document_ids = documentIds;
    if (mode === "solutions") body.challenge_id = challengeId;

    const response = yield call(fetch, endpoint, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) throw new Error("Failed to send message to AI");

    const data = yield response.json();
    const normalizedReplySources = mode === "pdf"
      ? normalizeSources(data.source_details || data.sources)
      : normalizeSources(data.sources || []);

    yield put(sendMessageSuccess({
      role: "assistant",
      content: data.answer,
      sources: normalizedReplySources
    }));

  } catch (error) {
    yield put(sendMessageFailure(error.message));
    yield put(sendMessageSuccess({ role: "assistant", content: "Sorry, an error occurred." }));
  }
}

export function* watchAgentsSaga() {
  yield takeLatest(fetchHistoryRequest.type, fetchHistorySaga);
  yield takeLatest(sendMessageRequest.type, sendMessageSaga);
  yield takeLatest(fetchSuggestionsRequest.type, fetchSuggestionsSaga);
}